import { useEffect, useMemo, useState } from "react";
import { Trophy, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthProvider.jsx";
import { fmtCurrencyBR } from "../../utils/formatBrl";

// Troféu que vai se preenchendo de baixo para cima conforme o faturamento: a
// parte conquistada fica colorida e o resto em cinza; em 100% fica inteiro.
function AwardTile({ award }) {
    const [imgError, setImgError] = useState(false);
    const locked = !award.unlocked;
    const progress = Math.max(0, Math.min(Number(award.progress) || 0, 100));
    const pctLabel = `${progress >= 10 ? Math.floor(progress) : progress.toFixed(1).replace(".0", "")}%`;

    // Começa vazio e anima até o progresso ao montar.
    const [fill, setFill] = useState(0);
    useEffect(() => {
        const id = requestAnimationFrame(() => setFill(progress));
        return () => cancelAnimationFrame(id);
    }, [progress]);

    let img = null;
    try {
        img = require(`../../static/images/milestones/${award.image}`);
    } catch {
        img = null;
    }
    const hasImg = img && !imgError;

    return (
        <div
            title={`${award.label}: ${pctLabel}`}
            className="flex flex-col gap-1.5 cursor-default"
        >
            <div
                className={`h-28 sm:h-36 p-2 rounded-xl border relative overflow-hidden flex items-center justify-center ${
                    locked
                        ? "bg-white/[0.02] border-white/[0.06]"
                        : "bg-[rgba(139,92,246,0.08)] border-transparent"
                }`}
            >
                {hasImg ? (
                    <div className="relative w-full h-full">
                        {/* Base em cinza */}
                        <img
                            src={img}
                            alt={award.label}
                            onError={() => setImgError(true)}
                            className={`absolute inset-0 w-full h-full object-contain ${locked ? "grayscale opacity-40" : ""}`}
                        />
                        {/* Parte conquistada, colorida, recortada de baixo para cima */}
                        {locked && (
                            <img
                                src={img}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 w-full h-full object-contain transition-[clip-path] duration-1000 ease-out"
                                style={{ clipPath: `inset(${100 - fill}% 0 0 0)` }}
                            />
                        )}
                    </div>
                ) : (
                    <>
                        {locked && (
                            <div
                                className="absolute inset-x-0 bottom-0 bg-[rgba(139,92,246,0.18)] transition-[height] duration-1000 ease-out"
                                style={{ height: `${fill}%` }}
                            />
                        )}
                        {locked ? (
                            <Lock size={16} strokeWidth={2.5} className="relative text-white/30" />
                        ) : (
                            <Trophy size={16} strokeWidth={2.5} className="relative text-[var(--purple)]" />
                        )}
                    </>
                )}

                {locked && (
                    <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-md bg-black/60 text-[10px] font-bold text-white/85 tabular-nums">
                        {pctLabel}
                    </span>
                )}
            </div>

            <div
                className={`py-1 rounded-md flex items-center justify-center text-[8px] font-bold uppercase tracking-wide border ${
                    locked
                        ? "bg-white/[0.02] text-white/40 border-white/10"
                        : "bg-[rgba(139,92,246,0.12)] text-[var(--purple)] border-transparent"
                }`}
            >
                {award.label}
            </div>
        </div>
    );
}

// Ladder de fallback (mesmos níveis do backend). Usado enquanto o /me ainda
// não devolver `awards` — assim o card aparece de qualquer jeito.
const DEFAULT_LADDER = [
    { target: 100000, label: "R$ 100 mil", color: "#22c55e", image: "100k.png" },
    { target: 500000, label: "R$ 500 mil", color: "#3b82f6", image: "500k.png" },
    { target: 1000000, label: "R$ 1 milhão", color: "#a855f7", image: "1m.png" },
];

export default function Premiacoes() {
    const { user } = useAuth();

    const total = Number(
        user?.lifetime_revenue ?? user?.milestones?.[0]?.current ?? 0
    );

    const awards = useMemo(() => {
        if (Array.isArray(user?.awards) && user.awards.length > 0) return user.awards;
        // Fallback: monta o ladder a partir do faturamento total.
        return DEFAULT_LADDER.map((m) => ({
            ...m,
            unlocked: total >= m.target,
            progress: Math.min((total / m.target) * 100, 100),
        }));
    }, [user, total]);

    const nextGoal = useMemo(() => awards.find((a) => !a.unlocked) || null, [awards]);

    if (awards.length === 0) return null;

    return (
        <div className="ob-card mt-3 mb-3 lg:flex-1 flex flex-col">
            <div className="p-4 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3">
                    <Trophy size={14} strokeWidth={2.5} className="text-[var(--purple)]" aria-hidden="true" />
                    <h3 className="font-semibold text-white text-sm">Premiações</h3>
                </div>

                <div
                    className="grid gap-2"
                    style={{ gridTemplateColumns: `repeat(${awards.length}, minmax(0, 1fr))` }}
                >
                    {awards.map((a) => (
                        <AwardTile key={a.target} award={a} />
                    ))}
                </div>

                <div className="mt-auto pt-3 border-t border-white/5">
                    <div className="flex items-center justify-between text-[10px]">
                        <span className="text-white/40 font-medium">Faturamento total</span>
                        <span className="text-white font-bold">{fmtCurrencyBR.format(total)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] mt-1">
                        <span className="text-white/40">Próxima meta</span>
                        <span className="text-[var(--purple)] font-bold">
                            {nextGoal
                                ? `+${fmtCurrencyBR.format(nextGoal.target - total)}`
                                : "🎉 Tudo desbloqueado!"}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
